import { api } from '../api/client'
import type { PriceQuery, Quote } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions } from '../AppLayout'
import { useListParams } from '../hooks/useListParams'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { EmptyState, ErrorBox, Note } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import { InstrumentLink, geld, zeitpunkt }
  from './priceFormat'

const PERMISSION = 'quote:read'
const PAGE_SIZE = 50

const COLUMNS: Column<Quote>[] = [
  { key: 'name', label: 'Instrument', sortable: true, minWidth: '220px',
    render: (row) => <InstrumentLink id={row.instrument_id} name={row.name} /> },
  { key: 'isin', label: 'ISIN', sortable: true, mono: true, copy: true,
    minWidth: '190px', hideOn: 'tablet' },
  { key: 'exchange', label: 'Handelsplatz', sortable: true, mono: true,
    minWidth: '130px',
    help: 'Der Platz, an dem der Kurs beobachtet wurde. Derselbe Titel hat zur selben Zeit an zwei Plätzen zwei verschiedene Kurse — in unterschiedlichen Währungen.' },
  { key: 'observed_at', label: 'Zeitpunkt', sortable: true, mono: true,
    minWidth: '160px',
    render: (row) => zeitpunkt.format(new Date(row.observed_at)),
    help: 'Zeitpunkt der Beobachtung — nicht der Speicherung.' },
  // Waehrung und Betrag in EINER Spalte: Sie gehoeren zusammen, und die
  // Umrechnung braucht ohnehin beides nebeneinander.
  { key: 'price', label: 'Kurs', sortable: true, mono: true, align: 'right',
    minWidth: '150px',
    // Kopiert wird der GEHANDELTE Betrag, nicht der umgerechnete: Er ist der
    // gemessene Wert, der andere ist abgeleitet.
    copy: (row) => row.price.amount,
    render: (row) => geld(row.price) },
]

export function QuotesPage() {
  const allowed = useHasPermission(PERMISSION)

  const list = useListParams({
    // `currency` steht NICHT dabei: Ein Filter entscheidet, WELCHE Zeilen
    // kommen — die Zielwährung nur, wie sie aussehen. Sie hängt an der
    // Sitzung und nicht an dieser Liste, siehe `useZielwaehrung`.
    filters: { q: '', exchange: '', since: '', until: '' },
    sort: 'observed_at',
    // Bei Kursen interessiert der jüngste zuerst — anders als bei Instrumenten,
    // wo alphabetisch das Natürliche ist.
    descending: true,
  })

  const { waehrung, wahl } = useZielwaehrung(allowed)

  // OBEN IN DER KOPFZEILE, nicht in der Filterleiste: Ein Filter entscheidet,
  // WELCHE Zeilen kommen — die Zielwährung nur, wie sie aussehen. Sie gilt für
  // die ganze Ansicht und ist von der Tabelle losgelöst.
  //
  // `useMemo`, weil `usePageActions` das Element als Abhängigkeit führt: Ein
  // bei jedem Rendern neu erzeugtes JSX setzte den Zustand endlos neu.
  usePageActions(wahl)

  const params: PriceQuery = {
    q: list.filters.q.trim() || undefined,
    exchange: list.filters.exchange.trim().toUpperCase() || undefined,
    // Als Liste, obwohl die Auswahl nur eine zulässt: Die Schnittstelle nimmt
    // mehrere und rechnet in jede um.
    currency: waehrung ? [waehrung] : undefined,
    // Das Feld liefert `2026-08-18`; der Endpunkt erwartet einen Zeitpunkt.
    // Ohne Uhrzeit begänne der Tag um 00:00 der Zeitzone des Servers.
    since: list.filters.since ? `${list.filters.since}T00:00:00Z` : undefined,
    until: list.filters.until ? `${list.filters.until}T23:59:59Z` : undefined,
    sort: list.sort,
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
  }

  const { result, loading, error, denied } = useListQuery(
    api.quotes, params, allowed)

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION}
                             subject="die Kurse" />
  }

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Name oder Symbol — mindestens zwei Zeichen"
                 value={list.filters.q} autoFocus
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Input label="Handelsplatz" placeholder="z. B. NASDAQ"
                 value={list.filters.exchange}
                 onChange={(event) =>
                   list.setFilter('exchange', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.since}
                 onChange={(event) =>
                   list.setFilter('since', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.until}
                 onChange={(event) =>
                   list.setFilter('until', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card flush span={12}>
        <DataTable
          columns={COLUMNS}
          rows={result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={result ? { page: result.page, size: result.size,
                           total: result.total, hasMore: result.has_more }
                       : undefined}
          onPage={list.setPage}
          loading={loading && result === null}
          empty={<EmptyState title="keine Kurse">
            {list.touched
              ? 'Kein Kurs passt zu diesen Filtern.'
              : 'Es sind noch keine Kurse erfasst.'}
          </EmptyState>}
        />
      </Card>

      <Note>
        Die Gesamtzahl wird nicht mitgezählt: Bei Kursen über alle Instrumente
        kostet das ein `count(*)` über die ganze Tabelle. Geblättert wird über
        Vor und Zurück.
      </Note>
    </>
  )
}
